import { Screen, PrimaryButton } from '../ui/Screen.jsx'

/**
 * The crisis screen. Three levels, all copy from config, resource list keyed on
 * the participant's country.
 *
 * Absolutes, all of them config's and enforced by config's validator too:
 *  - NEVER names a method or means
 *  - NEVER claims anyone has been notified. v0 has no clinician notification,
 *    and telling someone reporting suicidal thoughts that help was alerted when
 *    it was not is the worst thing this app could do.
 *  - shows no score, no band and no interpretation of what they answered
 *
 * DS-12 §1.1 — WHAT `dismissal_required` DECIDES, AND WHAT IT DOES NOT.
 *
 * It does NOT decide whether this screen appears. Every level interposes and
 * every level costs a deliberate tap; a safety screen is never skippable.
 *
 * It decides ONE thing: whether the control is DISPLACED.
 *
 *  - true  (level 3) — full viewport, nothing behind it, the action pushed to
 *    the far end of the screen, at least --s-12 from where the previous
 *    screen's primary action sat. Muscle memory and a double-tap cannot clear
 *    it.
 *  - false (levels 1-2) — a card in the flow, action in the normal position,
 *    one tap. She is still stopped; a reflex can still clear it.
 *
 * NOTHING ABOUT SIZE, WEIGHT OR COLOUR DIFFERS BETWEEN LEVELS (DS-5 §5.3). The
 * temptation, once you accept that levels should differ, is to make level 3
 * louder. It must not be: she must never be able to read a severity grade off
 * the interface. Only the geometry changes.
 *
 * This flag used to be computed at both call sites and passed to neither, so
 * config set it, design specified it, and it decided nothing — the exact state
 * DS-12 §1 says is worse than not having the flag at all.
 *
 * The button's words are config's `cta`, never this component's: Continue at
 * levels 1-2, "I have read this" at level 3. Same reason as every other string
 * here.
 *
 * A resource with phone: null must NOT render as a call button. Every number in
 * config is currently a TODO, so a naive tel: link would produce a dead dial on
 * the one screen where that is least acceptable.
 */
export default function SafetyScreen({
  level,
  resources,
  countryName,
  onDismiss,
  dismissalRequired = false,
  /*
   * A screen that borrows this composition (the support sighting) puts ITS
   * forward action here so it lands in the band.
   *
   * It used to render its own control as a SIBLING of this component, outside
   * Screen. That was already the one region in the app outside the shell (F13);
   * once the shell became `fixed inset-0` it stopped being reachable at all —
   * the fixed shell fills the viewport and a sibling in document flow, on a
   * document that no longer scrolls, is simply not on screen. The walk found it
   * by trying to press Continue and waiting thirty seconds.
   */
  action = null,
}) {
  if (!level) return null

  // crisisContent has already filtered these to what can be acted on, and
  // emptied them entirely when the state is `none`.
  const contactable = (resources ?? []).filter((r) => r.phone || r.url)

  return (
    <Screen action={action}>
      {/*
        Identical chrome at every level — same border, same surface, same
        padding, same type. The only thing `dismissalRequired` changes is that
        the card claims the whole viewport so the action can be displaced to the
        far end of it.
      */}
      <div
        className={
          'rounded-[var(--r-surface)] border border-[var(--line)] bg-[var(--surface-raised)] p-5' +
          (dismissalRequired ? ' flex min-h-[80dvh] flex-col' : '')
        }
        data-dismissal-required={dismissalRequired ? '1' : undefined}
      >
        {/*
          §4 — THE ONE PLACE IN THIS SYSTEM WHERE A TOKEN MAY BE LARGER.
          Every h1 in the app used to be the same 20px/600, so "Please get help
          now" carried exactly as much typographic weight as "Create your
          account". --t-safety is the step above the question and this is the
          only screen that uses it. It is the same at all three levels: the
          heading outranks the chrome, never the other levels.

          The heading is a SLOT, not a string — config conditions titles on
          resource state exactly as it does bodies, so there are seven distinct
          titles across three levels. Never lay out around one, never assume a
          line count.
        */}
        <h1 className="t-safety text-[var(--ink-900)]">{level.title}</h1>
        <p className="mt-3 whitespace-pre-line text-base leading-relaxed text-[var(--ink-900)]">
          {level.body}
        </p>

        {/*
          NOTHING IS RENDERED HERE UNLESS IT CAN BE ACTED ON.
          There is no empty-list branch and no placeholder, because the body copy
          for `none` and `default_only` already says the honest thing. A heading
          over an empty space, or a list of labels with no numbers, reads as help
          and is not — and this is the screen where that costs the most.
        */}
        {level.show_resources !== false && contactable.length ? (
          <div className="mt-5">
            {countryName ? (
              <h2 className="mb-2 text-base font-semibold text-[var(--ink-900)]">{countryName}</h2>
            ) : null}

            <ul className="flex flex-col gap-2">
                {contactable.map((resource) => (
                  <li
                    key={resource.label}
                    className="rounded-[var(--r-surface)] border border-[var(--line)] bg-[var(--surface)] px-4 py-3"
                  >
                    <p className="text-base font-semibold text-[var(--ink-900)]">{resource.label}</p>
                    {resource.hours ? (
                      <p className="text-sm text-[var(--ink-700)]">{resource.hours}</p>
                    ) : null}

                    {/*
                      §4 — FULL-WIDTH SECONDARY BUTTONS AT 48px, NEVER INLINE
                      TEXT A SHAKING HAND HAS TO HIT. These were an
                      inline-block pill and an underlined text link; both are
                      small targets, and this is the screen where a mis-tap
                      costs the most.

                      Secondary, not primary: the dismissal control is the only
                      --accent fill on this screen, so a resource must not
                      compete with it or read as the way out.
                    */}
                    {resource.phone ? (
                      <a
                        href={`tel:${resource.phone.replace(/\s+/g, '')}`}
                        className={
                          'mt-[var(--s-3)] flex min-h-[max(48px,3rem)] w-full items-center justify-center ' +
                          'rounded-[var(--r-control)] border border-[var(--control-border)] ' +
                          'bg-[var(--surface)] px-[var(--s-4)] text-base font-semibold text-[var(--accent)]'
                        }
                      >
                        Call {resource.phone}
                      </a>
                    ) : null}

                    {resource.url ? (
                      <a
                        href={resource.url}
                        target="_blank"
                        rel="noreferrer"
                        className={
                          'mt-[var(--s-2)] flex min-h-[max(48px,3rem)] w-full items-center justify-center ' +
                          'rounded-[var(--r-control)] border border-[var(--control-border)] ' +
                          'bg-[var(--surface)] px-[var(--s-4)] text-base font-semibold text-[var(--accent)]'
                        }
                      >
                        Open website
                      </a>
                    ) : null}
                  </li>
                ))}
            </ul>
          </div>
        ) : null}

        {/*
          THE ONE DIFFERENCE BETWEEN LEVELS, and it is geometry only.

          Displaced: mt-auto drops the action to the far end of a viewport-tall
          card, and pt guarantees at least --s-12 of clear space above it even
          when the copy and resources already fill the screen. Either way it is
          nowhere near where Continue sat on the item she just left, so the
          second half of a double-tap lands on nothing.

          Normal: the ordinary action position, one tap, same button.

          The label is config's. Continue at levels 1-2, "I have read this" at
          level 3 — a string this file must never author, because the words on a
          crisis screen are governed and swept exactly like every other
          participant-facing string.
        */}
        {/*
          NO HANDLER, NO CONTROL. The support screen reaches this with
          onDismiss null because nothing triggered it and nothing is being
          dismissed — and a cta reading "Continue" there would imply she had
          been sent somewhere, which is the exact reading the permanent
          affordance exists to prevent. A dead button is also its own defect on
          this screen.
        */}
        {onDismiss ? (
          <div className={dismissalRequired ? 'mt-auto pt-[var(--s-12)]' : 'mt-6'}>
            <PrimaryButton onClick={onDismiss}>{level.cta || 'Continue'}</PrimaryButton>
          </div>
        ) : null}
      </div>
    </Screen>
  )
}

/** A resource a participant could actually act on. Anything else is decoration. */
const isContactable = (resource) => Boolean(resource?.phone || resource?.url)

/**
 * Which crisis copy applies, decided by WHAT CAN ACTUALLY BE RENDERED rather
 * than by what the country block claims exists.
 *
 * A country block whose only entry has phone: null is `none` in practice, and
 * that is the live case today: every one of config's nine resource entries
 * still has a null phone, so GB reports matched_country: true and yet has
 * nothing to show. Trusting matched_country here would tell a participant in
 * London that "the services below are free and available now" above an empty
 * space, at the worst moment of their five days.
 *
 * `default_only` matters just as much and is permanent, not a gap waiting on
 * phone numbers: config holds 5 country blocks and registration accepts 251
 * countries. Someone in Nigeria shown a UK number and told services are
 * available now is worse off than being told nothing, because it costs them the
 * attempt.
 */
export function crisisState(payload) {
  // The SERVER owns this rule (config #61): GET /api/safety/resources returns
  // `state`, and two implementations of displayability is how the first one
  // drifts — on the screen that decides what someone at a level-3 threshold
  // reads. Take it when it is there; compute it only while it is not.
  if (typeof payload?.state === 'string' && payload.state) return payload.state

  const contactable = (payload?.resources ?? []).filter(isContactable)
  if (!contactable.length) return 'none'
  return payload?.matched_country ? 'local' : 'default_only'
}

/**
 * Copy may only ever degrade toward promising LESS.
 *
 * If the exact body for a state is missing, a weaker one is acceptable and a
 * stronger one never is: showing `none` wording to someone who has local
 * numbers is merely unhelpful, while showing `local` wording to someone with
 * nothing is a false promise on a crisis screen.
 */
const NO_STRONGER_THAN = {
  local: ['local', 'default_only', 'none'],
  default_only: ['default_only', 'none'],
  none: ['none'],
}

/**
 * One accessor for every state-keyed field. `title` and `body` have the same
 * three keys, so they take the same path — and both must, because "Support is
 * available" sitting above "It cannot give you a support number for where you
 * are" is the worse half of that defect, not the lesser one. The title is read
 * first.
 *
 * A bare string is NOT trusted to match a state. config broke both fields from
 * strings to maps deliberately, so a client still reading them as strings fails
 * loudly rather than silently showing local copy to someone with nothing.
 */
function copyFor(levelCopy, field, state) {
  const value = levelCopy?.[field]
  if (typeof value !== 'object' || value === null) return ''
  for (const key of NO_STRONGER_THAN[state] ?? ['none']) {
    if (typeof value[key] === 'string' && value[key].trim()) return value[key]
  }
  return ''
}

/**
 * Shape the response of GET /api/safety/resources into what the screen renders.
 *
 * The country comes from the SERVER: it defaults to the participant's own
 * demo_country and falls back to the default block. The client never has to
 * know which countries have entries. What the client does decide is whether
 * anything is actually showable, because only it knows what it can render.
 *
 * @param {object} payload {country, matched_country, levels, resources}
 * @param {number|string} level the level the server assigned
 */
export function crisisContent(payload, level) {
  if (!payload || !level) return null
  const levelCopy = payload.levels?.[String(level)]
  if (!levelCopy) return null

  const state = crisisState(payload)
  const contactable = (payload.resources ?? []).filter(isContactable)

  return {
    state,
    // title and body both resolve through the state. cta stays a single string
    // and is checked as one, because it shows in all three states.
    level: {
      ...levelCopy,
      title: copyFor(levelCopy, 'title', state),
      body: copyFor(levelCopy, 'body', state),
    },
    // Only ever the ones that can be acted on. A list of labels with no number
    // is worse than no list: it looks like help and is not.
    resources: state === 'none' ? [] : contactable,
    countryName: '',
    dismissalRequired: levelCopy.dismissal_required === true,
  }
}
